import {
  useGetMediaDownloadUrlQuery,
  useGetMediaQuery,
} from "@package/ui-gql/index-data-access";
import { produce } from "immer";
import { get } from "lodash";
import type { PropsWithChildren } from "react";
import { useCallback, useState } from "react";
import { type OnDocumentLoadSuccess } from "react-pdf/dist/shared/types.js";
import { type FlyoutProps } from "../../../utils/flyout-props";
import {
  defaultFlyoutPdfContextState,
  FlyoutPdfStateContext,
} from "./context-flyout-pdf";

type FlyoutPdfStateProviderProps = {
  screen: FlyoutProps["screen"];
};

export function FlyoutPdfStateProvider({
  screen,
  children,
}: PropsWithChildren<FlyoutPdfStateProviderProps>) {
  const [state, setState] = useState(defaultFlyoutPdfContextState);

  const remoteUrl = get(screen?.flyout?.post, "customData.pdf.hostedUrl");
  const mediaId = get(screen?.flyout?.post, "customData.pdf.media");
  const {
    data: dataMedia,
    loading: loadingMedia,
    error: errorMedia,
  } = useGetMediaQuery({
    variables: {
      id: mediaId ?? "",
    },
    skip: !mediaId,
  });
  const {
    data: dataMediaUrl,
    loading: loadingMediaUrl,
    error: errorMediaUrl,
  } = useGetMediaDownloadUrlQuery({
    variables: {
      fileName: dataMedia?.item?.file ?? "",
    },
    skip: !dataMedia?.item?.file,
  });
  const file = remoteUrl || dataMediaUrl?.item?.url || undefined;

  const onLoadSuccess = useCallback<OnDocumentLoadSuccess>(({ numPages }) => {
    setState((prev) =>
      produce(prev, (draft) => {
        draft.pageCount = numPages;
      }),
    );
  }, []);
  const incrementPage = useCallback(() => {
    setState((prev) =>
      produce(prev, (draft) => {
        if (prev.pageCurrent + 1 <= prev.pageCount) {
          draft.pageCurrent = prev.pageCurrent + 1;
        }
      }),
    );
  }, []);
  const decrementPage = useCallback(() => {
    setState((prev) =>
      produce(prev, (draft) => {
        if (prev.pageCurrent - 1 >= 1) {
          draft.pageCurrent = prev.pageCurrent - 1;
        }
      }),
    );
  }, []);
  const setPage = useCallback((page: number) => {
    setState((prev) =>
      produce(prev, (draft) => {
        if (page >= 1 && page <= prev.pageCount) {
          draft.pageCurrent = page;
        }
      }),
    );
  }, []);

  return (
    <FlyoutPdfStateContext.Provider
      value={{
        ...state,
        error: !!(errorMedia || errorMediaUrl),
        file,
        loading: loadingMedia || loadingMediaUrl,
        incrementPage,
        decrementPage,
        onLoadSuccess,
        setPage,
      }}
    >
      {children}
    </FlyoutPdfStateContext.Provider>
  );
}
